import { addProjectApprovalComment, approveReview, downloadApprovalReviewReport, downloadArtifact, getApprovalReview, getProjectApprovalDetail, rejectReview, saveReviewComments, type CoworkApprovalComment, type CoworkApprovalDetail, type CoworkApprovalReview } from "@/cowork/api"
import { useLanguage } from "@/context/language"
import { useParams } from "@solidjs/router"
import { createResource, For, onCleanup, Show } from "solid-js"
import { createStore } from "solid-js/store"
import { CoworkBrand } from "@/cowork/brand"
import { PublicReportPreview } from "@/components/public-report-preview"
import { ButtonV2 } from "@opencode-ai/ui/v2/button-v2"
import { IconButtonV2 } from "@opencode-ai/ui/v2/icon-button-v2"
import { Icon as IconV2 } from "@opencode-ai/ui/v2/icon"

export default function CoworkReviewPage() {
  const params = useParams<{ approvalID: string }>()
  return <Show when={params.approvalID} keyed>{(approvalID) => <ReviewView approvalID={approvalID} />}</Show>
}

export function ReviewView(props: {
  approvalID: string
  workspace?: { projectID: string; companyID: string; label: string }
  onUpdated?: (value: CoworkApprovalReview) => void
}) {
  const language = useLanguage()
  const workspace = props.workspace
  const [review, actions] = createResource<CoworkApprovalReview & Partial<CoworkApprovalDetail>, string>(
    () => props.approvalID,
    (id) => workspace ? getProjectApprovalDetail(workspace.projectID, id, workspace.companyID) : getApprovalReview(id),
  )
  const canComment = () => !workspace || review()?.can_comment === true
  const [store, setStore] = createStore({ draft: "", submitting: "", error: "", saved: false, panelCollapsed: false })
  const panelID = () => `review-comments-${props.approvalID}`
  let disposed = false
  onCleanup(() => { disposed = true })
  const history = (): CoworkApprovalComment[] => {
    const value = review()
    if (value?.comment_history?.length) return value.comment_history
    return value?.comments ? [{ id: "legacy", kind: "legacy", body: value.comments, created_at: value.updated_at }] : []
  }
  const submit = async (action: "comment" | "approve" | "reject") => {
    if (disposed || store.submitting || (action === "comment" && !store.draft.trim())) return
    if (workspace && (action !== "comment" || !canComment())) return
    const id = props.approvalID
    const draft = store.draft.trim()
    setStore({ submitting: action, error: "", saved: false })
    try {
      const value = await (workspace ? addProjectApprovalComment(workspace.projectID, id, draft, workspace.companyID)
        : action === "comment" ? saveReviewComments(id, draft)
        : action === "approve" ? approveReview(id, draft || undefined) : rejectReview(id, draft || undefined))
      if (disposed || props.approvalID !== id) return
      actions.mutate(value)
      setStore({ draft: "", saved: action === "comment" })
      props.onUpdated?.(value)
    } catch {
      if (!disposed && props.approvalID === id) setStore("error", language.t("cowork.review.actionError"))
    } finally {
      if (!disposed && props.approvalID === id) setStore("submitting", "")
    }
  }
  return (
    <div class="flex min-h-0 flex-col bg-v2-background-bg-deep text-v2-text-text-base" classList={{ "h-dvh": !workspace, "h-full": !!workspace }}>
      <Show when={!review.loading} fallback={<p role="status" class="m-auto p-6 text-13-regular">{language.t("cowork.review.loading")}</p>}>
        <Show when={!review.error} fallback={<p role="alert" class="m-auto p-6 text-13-regular">{language.t("cowork.review.unavailable")}</p>}>
          <Show when={review()}>{(value) => (
            <>
              <header class="flex shrink-0 flex-wrap items-center gap-x-6 gap-y-3 border-b border-v2-border-border-base bg-v2-background-bg-base px-5 py-4">
                <Show when={!workspace}><CoworkBrand size={28} /></Show>
                <div class="min-w-0 flex-1">
                  <h1 class="truncate text-[18px] font-semibold leading-6">{value().report_title}</h1>
                  <div class="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-12-regular text-v2-text-text-muted">
                    <Show when={workspace}><span>{workspace?.label}</span></Show>
                    <span>{language.t("cowork.review.requestedBy", { user: value().requested_by_name || value().requested_by_email || value().requested_by_user_id })}</span>
                    <span>{value().approver_email}</span>
                  </div>
                </div>
                <ReviewStatus status={value().status} />
              </header>

              <main class="grid min-h-0 flex-1 gap-4 overflow-y-auto p-4 lg:overflow-hidden" classList={{
                "grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px] xl:grid-cols-[minmax(0,1fr)_380px]": !store.panelCollapsed,
                "grid-cols-[minmax(0,1fr)_40px]": store.panelCollapsed,
              }}>
                <div class="flex min-h-[560px] min-w-0 flex-col lg:min-h-0">
                  <PublicReportPreview title={value().report_title} format={value().report_format}
                    filename={value().report_filename} createdAt={value().created_at}
                    load={() => workspace
                      ? downloadArtifact(workspace.projectID, value().report_artifact_id!, workspace.companyID)
                      : downloadApprovalReviewReport(props.approvalID)} />
                </div>

                <div class="relative flex min-h-0 min-w-0">
                  <IconButtonV2
                    class="absolute right-2 top-3 z-10"
                    size="small"
                    variant="ghost"
                    icon={<IconV2 name="sidebar-right" size="small" />}
                    aria-label={language.t(store.panelCollapsed ? "cowork.review.panel.expand" : "cowork.review.panel.collapse")}
                    title={language.t(store.panelCollapsed ? "cowork.review.panel.expand" : "cowork.review.panel.collapse")}
                    aria-expanded={!store.panelCollapsed}
                    aria-controls={panelID()}
                    onClick={() => setStore("panelCollapsed", (collapsed) => !collapsed)}
                  />
                <aside id={panelID()} hidden={store.panelCollapsed} aria-label={language.t("cowork.review.panel.title")}
                  class="min-h-[440px] w-full flex-col overflow-hidden rounded-xl border border-v2-border-border-base bg-v2-background-bg-base lg:min-h-0"
                  classList={{ flex: !store.panelCollapsed, hidden: store.panelCollapsed }}>
                  <div class="shrink-0 border-b border-v2-border-border-base p-4">
                    <h2 class="pr-8 text-[14px] font-semibold">{language.t("cowork.review.panel.title")}</h2>
                    <p class="mt-1 text-12-regular text-v2-text-text-muted">
                      {language.t(workspace ? "cowork.enterprise.approvals.conversationDescription" : "cowork.review.panel.description")}
                    </p>
                  </div>
                  <div class="min-h-0 flex-1 overflow-y-auto p-4" aria-label={language.t("cowork.review.history.title")}>
                    <h3 class="mb-3 text-12-medium text-v2-text-text-muted">{language.t("cowork.review.history.title")}</h3>
                    <Show when={history().length} fallback={<p class="text-12-regular text-v2-text-text-muted">{language.t("cowork.review.history.empty")}</p>}>
                      <ol class="space-y-4">
                        <For each={history()}>{(comment) => (
                          <li class="rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 p-3">
                            <div class="break-words text-12-medium">{comment.author_name || comment.author_email || language.t("cowork.review.history.previous")}</div>
                            <time dateTime={comment.created_at} class="mt-1 block text-11-regular text-v2-text-text-muted">
                              {new Date(comment.created_at).toLocaleString(language.locale())}
                            </time>
                            <Show when={comment.kind !== "comment" && comment.kind !== "legacy"}>
                              <p class="mt-2 text-12-medium">{language.t(`cowork.review.history.${comment.kind}` as "cowork.review.history.request" | "cowork.review.history.approved" | "cowork.review.history.rejected")}</p>
                            </Show>
                            <Show when={comment.body}><p class="mt-2 whitespace-pre-wrap break-words text-13-regular leading-relaxed">{comment.body}</p></Show>
                          </li>
                        )}</For>
                      </ol>
                    </Show>
                  </div>
                  <Show when={canComment()} fallback={
                    <p class="shrink-0 border-t border-v2-border-border-base p-4 text-12-regular text-v2-text-text-muted">
                      {language.t("cowork.enterprise.approvals.readOnly")}
                    </p>
                  }>
                  <form class="shrink-0 border-t border-v2-border-border-base p-4" onSubmit={(event) => { event.preventDefault(); void submit("comment") }}>
                    <label class="flex flex-col gap-2 text-12-medium">
                      {language.t("cowork.review.addComment")}
                      <textarea class="min-h-24 max-h-40 resize-y rounded-lg border border-v2-border-border-base bg-v2-background-bg-base p-3 text-13-regular outline-none focus:border-v2-border-border-active"
                        value={store.draft} disabled={!!store.submitting} maxLength={5000}
                        placeholder={language.t("cowork.review.commentPlaceholder")}
                        onInput={(event) => setStore({ draft: event.currentTarget.value, saved: false })} />
                    </label>
                    <Show when={store.error}><p role="alert" class="mt-2 text-12-regular text-v2-state-fg-danger">{store.error}</p></Show>
                    <Show when={store.saved}><p role="status" class="mt-2 text-12-regular text-v2-state-fg-success">{language.t("cowork.review.commentAdded")}</p></Show>
                    <div class="mt-3 flex justify-end">
                      <ButtonV2 type="submit" size="small" variant="outline" disabled={!!store.submitting || !store.draft.trim()}>
                        {language.t(store.submitting === "comment" ? "cowork.review.savingComments" : "cowork.review.postComment")}
                      </ButtonV2>
                    </div>
                  </form>
                  </Show>
                  <Show when={!workspace}>
                  <div class="shrink-0 border-t border-v2-border-border-base p-4">
                    <div class="flex gap-2">
                      <ButtonV2 class="flex-1" variant="outline" disabled={!!store.submitting || value().status === "rejected"} onClick={() => void submit("reject")}>
                        {language.t(store.submitting === "reject" ? "cowork.review.rejecting" : "cowork.review.reject")}
                      </ButtonV2>
                      <ButtonV2 class="flex-1" variant="contrast" disabled={!!store.submitting || value().status === "approved"} onClick={() => void submit("approve")}>
                        {language.t(store.submitting === "approve" ? "cowork.review.approving" : "cowork.review.approve")}
                      </ButtonV2>
                    </div>
                  </div>
                  </Show>
                </aside>
                </div>
              </main>
            </>
          )}</Show>
        </Show>
      </Show>
    </div>
  )
}

function ReviewStatus(props: { status: string }) {
  const language = useLanguage()
  const status = () => props.status === "approved" ? "approved" : props.status === "rejected" ? "rejected" : "pending"
  return (
    <span class="rounded-md px-3 py-1.5 text-12-medium" classList={{
      "bg-v2-state-bg-success text-v2-state-fg-success": status() === "approved",
      "bg-v2-state-bg-danger text-v2-state-fg-danger": status() === "rejected",
      "bg-v2-state-bg-warning text-v2-state-fg-warning": status() === "pending",
    }}>{language.t(`cowork.review.status.${status()}`)}</span>
  )
}
